// src/services/orderService.js
// ─────────────────────────────────────────────────────────────────
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "./firebase";

/**
 * Get all orders for the current user.
 */
export async function getUserOrders(userId) {
  const q = query(
    collection(db, "orders"),
    where("userId", "==", userId),
    orderBy("createdAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get a single order by ID.
 */
export async function getOrder(orderId) {
  const snap = await getDoc(doc(db, "orders", orderId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Get all orders (admin use).
 */
export async function getAllOrders(statusFilter = null, limitCount = 50) {
  let q = query(
    collection(db, "orders"),
    orderBy("createdAt", "desc"),
    limit(limitCount)
  );

  if (statusFilter) {
    q = query(
      collection(db, "orders"),
      where("status", "==", statusFilter),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );
  }

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}


// src/services/paymentService.js
// ─────────────────────────────────────────────────────────────────
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

const createOrderFn = httpsCallable(functions, "createOrder");
const checkStatusFn = httpsCallable(functions, "checkPaymentStatus");

/**
 * Initiate a purchase — calls Cloud Function to create order & get KHQR.
 *
 * @param {string} gameId
 * @returns {{ orderId, qrCodeData, amount, gameTitle, expiresAt }}
 */
export async function initiatePayment(gameId) {
  const result = await createOrderFn({ gameId });
  return result.data;
}

/**
 * Poll for payment status.
 * Call this every 3–5 seconds while showing the QR code.
 *
 * @param {string} orderId
 * @returns {{ status, credentials?, orderId, gameTitle, amount }}
 */
export async function pollPaymentStatus(orderId) {
  const result = await checkStatusFn({ orderId });
  return result.data;
}

/**
 * Helper: Generate a QR code image from KHQR string using the
 * qrcode.js library loaded in the browser (CDN).
 *
 * Usage: const dataUrl = await generateQRImage(qrCodeData)
 * Then: <img src={dataUrl} />
 */
export async function generateQRImage(qrString) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.QRCode) {
      reject(new Error("QRCode library not loaded"));
      return;
    }

    const canvas = document.createElement("canvas");
    window.QRCode.toCanvas(canvas, qrString, { width: 280, margin: 2 }, (err) => {
      if (err) reject(err);
      else resolve(canvas.toDataURL("image/png"));
    });
  });
}
